import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text, Icon } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import LocalizedCurrency from 'components/LocalizedCurrency';
import ExitAnimation from 'ui/atoms/ExitAnimation';
import { createTimerFormat } from 'utils/purchase/timer';
import { shouldShowUrgencyDiscountAvailability } from 'utils/urgencyDiscount';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

const META_PILL_ALTERNATE_MS = 10000;
const EXIT_ANIMATION_MS = 1000;
const MAX_DISCOUNT_TIMER_SECONDS = 24 * 60 * 60;
const MAX_TIMEOUT_MS = 2147483647;
const DISCOUNT_TIMER_THRESHOLDS_SECONDS = [3600, 300, 60];
const metaAlertClassName =
  'justify-end flex items-center gap-1.5 text-sm font-semibold leading-snug text-error-strong';
const secondTickSubscribers = new Set();
let secondTickIntervalId = null;

/**
 * Parses the discount expiration timestamp.
 *
 * @param {string} expiresAt - ISO expiration timestamp.
 * @returns {number|null} Expiration timestamp in milliseconds, or null when invalid.
 */
const getExpirationTimestamp = (expiresAt) => {
  if (!expiresAt) return null;

  const parsedExpiration = Date.parse(expiresAt);
  return Number.isNaN(parsedExpiration) ? null : parsedExpiration;
};

/**
 * Calculates remaining discount time in seconds.
 *
 * @param {string} expiresAt - ISO expiration timestamp.
 * @param {number} now - Current timestamp in milliseconds.
 * @returns {number} Remaining seconds until expiration.
 */
const getRemainingDiscountSeconds = (expiresAt, now = Date.now()) => {
  const expirationTimestamp = getExpirationTimestamp(expiresAt);
  if (!expirationTimestamp) return 0;

  return Math.max(0, Math.ceil((expirationTimestamp - now) / 1000));
};

/**
 * Checks whether the discount timer should be displayed.
 *
 * @param {number} remainingSeconds - Remaining seconds until expiration.
 * @returns {boolean} Whether the countdown is active.
 */
const isDiscountTimerActive = (remainingSeconds) =>
  remainingSeconds > 0 && remainingSeconds <= MAX_DISCOUNT_TIMER_SECONDS;

/**
 * Subscribes a callback to the shared one-second discount timer.
 *
 * @param {Function} callback - Function called on every second tick.
 * @returns {Function} Unsubscribe callback.
 */
const subscribeToSecondTick = (callback) => {
  secondTickSubscribers.add(callback);

  if (!secondTickIntervalId) {
    secondTickIntervalId = setInterval(() => {
      const now = Date.now();
      secondTickSubscribers.forEach((subscriber) => subscriber(now));
    }, 1000);
  }

  return () => {
    secondTickSubscribers.delete(callback);

    if (!secondTickSubscribers.size && secondTickIntervalId) {
      clearInterval(secondTickIntervalId);
      secondTickIntervalId = null;
    }
  };
};

/**
 * Returns the active countdown threshold in seconds, if any.
 *
 * @param {number} remainingSeconds - Remaining seconds until expiration.
 * @returns {number|null} Threshold in seconds, or null when above all thresholds.
 */
const getDiscountTimerThreshold = (remainingSeconds) => {
  if (remainingSeconds <= 0) return 0;

  return (
    DISCOUNT_TIMER_THRESHOLDS_SECONDS.find((threshold) => remainingSeconds <= threshold) ?? null
  );
};

/**
 * Visually hidden live region for screen reader announcements.
 *
 * @param {object} props - Component props.
 * @param {string} props.message - Message to announce.
 * @returns {JSX.Element|null} Live region or null when message is empty.
 */
const ScreenReaderAnnouncement = ({ message }) => {
  if (!message) return null;

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
};

ScreenReaderAnnouncement.propTypes = {
  message: PropTypes.string,
};

ScreenReaderAnnouncement.defaultProps = {
  message: '',
};

/**
 * Tracks remaining seconds until a discount expires.
 *
 * @param {string} expiresAt - ISO expiration timestamp.
 * @returns {number} Remaining seconds until expiration.
 */
const useDiscountCountdown = (expiresAt) => {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    const expirationTimestamp = getExpirationTimestamp(expiresAt);

    if (!expirationTimestamp) {
      setRemainingSeconds(0);
      return undefined;
    }

    let timeoutId = null;
    let unsubscribeFromSecondTick = null;

    /**
     * Updates the local countdown using the shared second tick timestamp.
     *
     * @param {number} now - Current timestamp in milliseconds.
     */
    const tick = (now = Date.now()) => {
      const nextRemainingSeconds = getRemainingDiscountSeconds(expiresAt, now);
      setRemainingSeconds(nextRemainingSeconds);

      if (nextRemainingSeconds <= 0 && unsubscribeFromSecondTick) {
        unsubscribeFromSecondTick();
        unsubscribeFromSecondTick = null;
      }
    };

    /**
     * Starts the shared one-second countdown updates.
     */
    const startSecondTick = () => {
      tick(Date.now());
      unsubscribeFromSecondTick = subscribeToSecondTick(tick);
    };

    /**
     * Wakes the timer when the discount enters the visible countdown window.
     */
    const scheduleActiveWindowWakeup = () => {
      const now = Date.now();
      const currentRemainingSeconds = getRemainingDiscountSeconds(expiresAt, now);
      setRemainingSeconds(currentRemainingSeconds);

      if (isDiscountTimerActive(currentRemainingSeconds)) {
        startSecondTick();
        return;
      }

      if (currentRemainingSeconds <= 0) return;

      const activeWindowStartDelay = expirationTimestamp - now - MAX_DISCOUNT_TIMER_SECONDS * 1000;
      timeoutId = setTimeout(
        scheduleActiveWindowWakeup,
        Math.min(Math.max(0, activeWindowStartDelay), MAX_TIMEOUT_MS),
      );
    };

    scheduleActiveWindowWakeup();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (unsubscribeFromSecondTick) unsubscribeFromSecondTick();
    };
  }, [expiresAt]);

  return remainingSeconds;
};

/**
 * Announces timer updates only on mount and when crossing countdown thresholds.
 *
 * @param {number} remainingSeconds - Remaining seconds until expiration.
 * @param {Function} t - i18n translate function scoped to search namespace.
 * @returns {string} Message for the screen reader live region.
 */
const useDiscountTimerAnnouncement = (remainingSeconds, t) => {
  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncedThresholdRef = useRef(null);
  const hasInitialAnnouncementRef = useRef(false);

  useEffect(() => {
    if (remainingSeconds <= 0) {
      if (lastAnnouncedThresholdRef.current === 0) return;

      lastAnnouncedThresholdRef.current = 0;
      setAnnouncement(t('discount_offer_ends_in', { time: createTimerFormat(0, 'HH:mm:ss') }));
      return;
    }

    const threshold = getDiscountTimerThreshold(remainingSeconds);

    if (!hasInitialAnnouncementRef.current) {
      hasInitialAnnouncementRef.current = true;
      lastAnnouncedThresholdRef.current = threshold;
      setAnnouncement(
        t('discount_offer_ends_in', {
          time: createTimerFormat(remainingSeconds, 'HH:mm:ss'),
        }),
      );
      return;
    }

    if (threshold !== null && threshold !== lastAnnouncedThresholdRef.current) {
      lastAnnouncedThresholdRef.current = threshold;
      setAnnouncement(
        t('discount_offer_ends_in', {
          time: createTimerFormat(threshold, 'HH:mm:ss'),
        }),
      );
    }
  }, [remainingSeconds, t]);

  return announcement;
};

/**
 * Announces meta pill changes when the visible message toggles or timer thresholds are crossed.
 *
 * @param {object} params - Hook params.
 * @param {boolean} params.isTimerVisible - Whether the timer pill is currently visible.
 * @param {number} params.count - Seats available at the discounted price.
 * @param {number} params.remainingSeconds - Remaining seconds until expiration.
 * @param {Function} params.t - i18n translate function scoped to search namespace.
 * @returns {string} Message for the screen reader live region.
 */
const useDiscountMetaPillAnnouncement = ({ isTimerVisible, count, remainingSeconds, t }) => {
  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncedThresholdRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const remainingSecondsRef = useRef(remainingSeconds);

  remainingSecondsRef.current = remainingSeconds;

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (isTimerVisible) {
      const seconds = remainingSecondsRef.current;
      if (seconds <= 0) return;

      const threshold = getDiscountTimerThreshold(seconds);
      lastAnnouncedThresholdRef.current = threshold;
      setAnnouncement(
        t('discount_offer_ends_in', {
          time: createTimerFormat(seconds, 'HH:mm:ss'),
        }),
      );
      return;
    }

    lastAnnouncedThresholdRef.current = null;
    setAnnouncement(t('discount_last_seats', { count }));
  }, [isTimerVisible, count, t]);

  useEffect(() => {
    if (!isTimerVisible || remainingSeconds <= 0) return;

    const threshold = getDiscountTimerThreshold(remainingSeconds);
    if (threshold === null || threshold === lastAnnouncedThresholdRef.current) return;

    lastAnnouncedThresholdRef.current = threshold;
    setAnnouncement(
      t('discount_offer_ends_in', {
        time: createTimerFormat(threshold, 'HH:mm:ss'),
      }),
    );
  }, [remainingSeconds, isTimerVisible, t]);

  return announcement;
};

/**
 * Renders the available seats pill content.
 *
 * @param {object} props - Component props.
 * @param {number} props.count - Number of seats available at the discounted price.
 * @returns {JSX.Element} The availability pill.
 */
const AvailableSeatsPill = ({ count }) => {
  const { t } = useTranslation('search');

  return (
    <div className={metaAlertClassName}>
      <span aria-hidden="true">🔥</span>
      <span>{t('discount_last_seats', { count })}</span>
    </div>
  );
};

AvailableSeatsPill.propTypes = {
  count: PropTypes.number.isRequired,
};

/**
 * Renders the discount timer pill content.
 *
 * @param {object} props - Component props.
 * @param {string} props.timeDisplay - Formatted remaining time.
 * @returns {JSX.Element} The timer pill.
 */
const TimerPill = ({ timeDisplay }) => {
  const { t } = useTranslation('search');

  return (
    <div className={metaAlertClassName}>
      <span aria-hidden="true">⏰</span>
      <span>{t('discount_offer_ends_in', { time: timeDisplay })}</span>
    </div>
  );
};

TimerPill.propTypes = {
  timeDisplay: PropTypes.string.isRequired,
};

/**
 * Alternates between the seats and timer pills with enter/exit fade animation.
 *
 * @param {object} props - Component props.
 * @param {number} props.count - Number of seats available at the discounted price.
 * @param {string} props.expiresAt - ISO expiration timestamp.
 * @returns {JSX.Element} The alternating meta pill.
 */
const DiscountMetaPillAlternator = ({ count, expiresAt }) => {
  const { t } = useTranslation('search');
  const [showSeatsState, setShowSeatsState] = useState(true);
  const [exitAnimation, setExitAnimation] = useState(false);
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const remainingSeconds = useDiscountCountdown(expiresAt);
  const showTimer = isDiscountTimerActive(remainingSeconds);
  const timeDisplay = createTimerFormat(Math.max(0, remainingSeconds), 'HH:mm:ss');
  const announcement = useDiscountMetaPillAnnouncement({
    isTimerVisible: !showSeatsState,
    count,
    remainingSeconds,
    t,
  });

  useEffect(() => {
    if (!showTimer) return undefined;

    const outAnimationTimeout = setTimeout(() => {
      setAnimationEnabled(true);
      setExitAnimation(true);
    }, META_PILL_ALTERNATE_MS - EXIT_ANIMATION_MS);

    const intoAnimationTimeout = setTimeout(() => {
      setShowSeatsState((previousState) => !previousState);
      setExitAnimation(false);
    }, META_PILL_ALTERNATE_MS);

    return () => {
      clearTimeout(outAnimationTimeout);
      clearTimeout(intoAnimationTimeout);
    };
  }, [showSeatsState, showTimer]);

  if (!showTimer) {
    return <AvailableSeatsPill count={count} />;
  }

  return (
    <>
      <div className="min-h-[1.25rem] w-full overflow-hidden">
        <div className="flex w-full justify-end">
          <ExitAnimation exitAnimation={exitAnimation} enabled={animationEnabled}>
            {showSeatsState ? (
              <AvailableSeatsPill count={count} />
            ) : (
              <TimerPill timeDisplay={timeDisplay} />
            )}
          </ExitAnimation>
        </div>
      </div>
      <ScreenReaderAnnouncement message={announcement} />
    </>
  );
};

DiscountMetaPillAlternator.propTypes = {
  count: PropTypes.number.isRequired,
  expiresAt: PropTypes.string.isRequired,
};

/**
 * Renders the discount percentage badge.
 *
 * @param {object} props - Component props.
 * @param {string} props.percent - Discount percentage label (e.g. "-20%").
 * @returns {JSX.Element|null} The discount badge or null when percent is missing.
 */
export const DiscountBadge = ({ percent }) => {
  if (!percent) return null;

  const displayPercent = String(percent).replace(/^-/, '');

  return (
    // --color-discount comes from the tenant theme via CSSVariablesProvider, falling back to the
    // :root default in _design-system.scss for brands that configure no discount colour.
    <span
      className="inline-flex items-center rounded-md bg-[var(--color-discount)] p-1.5 text-xs font-semibold leading-none text-white"
      aria-label={displayPercent}
    >
      {displayPercent}
    </span>
  );
};

DiscountBadge.propTypes = {
  percent: PropTypes.string,
};

DiscountBadge.defaultProps = {
  percent: null,
};

/**
 * Renders the limited discount seat availability pill.
 *
 * @param {object} props - Component props.
 * @param {number} props.count - Number of seats available at the discounted price.
 * @param {boolean} props.enabled - Whether the availability label is enabled.
 * @returns {JSX.Element|null} The availability pill or null when count is not positive.
 */
export const DiscountAvailableSeats = ({ count, enabled }) => {
  if (!enabled || !shouldShowUrgencyDiscountAvailability(count)) return null;

  return <AvailableSeatsPill count={count} />;
};

DiscountAvailableSeats.propTypes = {
  count: PropTypes.number,
  enabled: PropTypes.bool,
};

DiscountAvailableSeats.defaultProps = {
  count: null,
  enabled: true,
};

/**
 * Renders the discount expiration countdown pill.
 *
 * @param {object} props - Component props.
 * @param {string} props.expiresAt - ISO expiration timestamp.
 * @returns {JSX.Element|null} The timer pill or null when inactive.
 */
export const DiscountTimer = ({ expiresAt }) => {
  const { t } = useTranslation('search');
  const remainingSeconds = useDiscountCountdown(expiresAt);
  const announcement = useDiscountTimerAnnouncement(remainingSeconds, t);

  if (!expiresAt || !isDiscountTimerActive(remainingSeconds)) return null;

  const timeDisplay = createTimerFormat(remainingSeconds, 'HH:mm:ss');

  return (
    <>
      <TimerPill timeDisplay={timeDisplay} />
      <ScreenReaderAnnouncement message={announcement} />
    </>
  );
};

DiscountTimer.propTypes = {
  expiresAt: PropTypes.string,
};

DiscountTimer.defaultProps = {
  expiresAt: null,
};

/**
 * DynamicPricingDiscount component.
 * Renders the discount badge, price, limited seat availability and expiration timer for trip results.
 *
 * @param {object} props - Component props.
 * @param {number} props.total - Trip total price.
 * @param {string} props.discountPercent - Discount percentage label (e.g. "-20%").
 * @param {number} props.discountAvailability - Seats available at the discounted price.
 * @param {string} props.expiresAt - ISO discount expiration timestamp.
 * @param {string} props.priceColor - LocalizedCurrency color token.
 * @param {boolean} props.lighterText - Whether the price should use lighter styling.
 * @param {boolean} props.showFromLabel - Whether to show the "from" label before the price.
 * @param {string} props.discountIcon - Discount icon shown next to the percentage badge.
 * @param {node} props.secondaryPrice - Secondary price content rendered above the main price.
 * @param {string} props.priceSize - Desktop size for the primary price.
 * @param {string} props.mobilePriceSize - Mobile size for the primary price.
 * @returns {JSX.Element} The rendered discount block.
 */
const DynamicPricingDiscount = ({
  total,
  discountPercent,
  discountAvailability,
  expiresAt,
  priceColor,
  lighterText,
  showFromLabel,
  discountIcon,
  secondaryPrice,
  priceSize,
  mobilePriceSize,
}) => {
  const { t } = useTranslation();
  const features = useWhitelabelFeatures();
  const resolvedPriceColor = lighterText ? 'grayLight' : priceColor;
  const showTimer = Boolean(expiresAt);
  const showAvailabilityLabel = features.SHOW_DYNAMIC_PRICING_AVAILABILITY_LABEL;
  const showSeats =
    showAvailabilityLabel && shouldShowUrgencyDiscountAvailability(discountAvailability);
  const showMetaPills = showSeats || showTimer;
  const shouldAlternateMetaPills = showSeats && showTimer;

  /**
   * Renders the discount urgency metadata pill.
   *
   * @returns {JSX.Element|null} The seats pill, timer pill, or alternating pill.
   */
  const renderMetaPills = () => {
    if (shouldAlternateMetaPills) {
      return <DiscountMetaPillAlternator count={discountAvailability} expiresAt={expiresAt} />;
    }

    if (showSeats) {
      return (
        <DiscountAvailableSeats count={discountAvailability} enabled={showAvailabilityLabel} />
      );
    }

    return <DiscountTimer expiresAt={expiresAt} />;
  };

  const priceBlock = (
    <div className="flex w-full flex-col items-end gap-1">
      {secondaryPrice}
      <Spacing alignItems="center" size="XS">
        {showFromLabel && (
          <Text size="S" italic>
            {t('general:from')}
          </Text>
        )}
        {discountIcon && <Icon type={discountIcon} size="M" mobileSize="S" />}
        <DiscountBadge percent={discountPercent} />
        <LocalizedCurrency
          price={total}
          color={resolvedPriceColor}
          weight="bold"
          size={priceSize}
          mobileSize={mobilePriceSize}
          currencyStyle="currency"
        />
      </Spacing>
      {showMetaPills && renderMetaPills()}
    </div>
  );

  return <div className="fade-in animated w-full">{priceBlock}</div>;
};

DynamicPricingDiscount.propTypes = {
  total: PropTypes.number.isRequired,
  discountPercent: PropTypes.string,
  discountAvailability: PropTypes.number,
  expiresAt: PropTypes.string,
  priceColor: PropTypes.string,
  lighterText: PropTypes.bool,
  showFromLabel: PropTypes.bool,
  discountIcon: PropTypes.string,
  secondaryPrice: PropTypes.node,
  priceSize: PropTypes.string,
  mobilePriceSize: PropTypes.string,
};

DynamicPricingDiscount.defaultProps = {
  discountPercent: null,
  discountAvailability: null,
  expiresAt: null,
  priceColor: 'grayStrong',
  lighterText: false,
  showFromLabel: false,
  discountIcon: null,
  secondaryPrice: null,
  priceSize: 'XL',
  mobilePriceSize: 'L',
};

export default DynamicPricingDiscount;
